import { isNil, get, lstrip } from 'ldash';
import log from 'uix/logging';
export { switchLanguageFormat, formatDate, formatTime, formatDateTime, formatDistance, formatSeconds, formatMinutes } from './date';
/*
Formats the given number using `Number#toLocaleString`.
- If locale is a string, the value is expected to be a locale-key (for example: `de`).
- If locale is true, the system default locale is used for translation.
- If no value for locale is specified, the number is returned unmodified.
*/ const toLocaleString = (number, locale)=>{
    let result = number;
    if (typeof locale === 'string') {
        result = number.toLocaleString(locale);
    } else if (locale === true) {
        result = number.toLocaleString();
    }
    return result;
};
const SI_UNITS = [
    {
        value: 1e18,
        symbol: 'E'
    },
    {
        value: 1e15,
        symbol: 'P'
    },
    {
        value: 1e12,
        symbol: 'T'
    },
    {
        value: 1e9,
        symbol: 'G'
    },
    {
        value: 1e6,
        symbol: 'M'
    },
    {
        value: 1e3,
        symbol: 'k'
    },
    {
        value: 1,
        symbol: ''
    }
];
export const formatSI = (num, digits = 1)=>{
    /* formatSI(12345, 2) -> 12.3k
   */ const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    let i = SI_UNITS.findIndex((e)=>Math.abs(num) > e.value);
    i = i == -1 ? SI_UNITS.length - 1 : i;
    return (num / SI_UNITS[i].value).toFixed(digits).replace(rx, '$1') + SI_UNITS[i].symbol;
};
// https://github.com/sindresorhus/pretty-bytes
const BYTES_UNITS = [
    'B',
    'kB',
    'MB',
    'GB',
    'TB',
    'PB',
    'EB',
    'ZB',
    'YB'
];
export const prettyBytes = (number, options)=>{
    options = Object.assign({}, options);
    if (!Number.isFinite(number)) {
        if (options.fallback && isNil(number)) {
            // undefined and null
            return options.fallback;
        }
        log.error(`prettyBytes: Expected a finite number, got ${typeof number}: ${number}`);
        return ' ? B';
    }
    if (options.signed && number === 0) {
        return ' 0 B';
    }
    let isNegative = number < 0;
    let prefix = isNegative ? '-' : options.signed ? '+' : '';
    if (isNegative) {
        number = -number;
    }
    if (number < 1) {
        let n = toLocaleString(number, options.locale);
        return prefix + n + ' B';
    }
    let exponent = Math.min(Math.floor(Math.log10(number) / 3), BYTES_UNITS.length - 1);
    number = Number(number / Math.pow(1000, exponent));
    let precision = options.precision ?? 3;
    let n = '';
    if (precision == 0) {
        // precision = 0 rounds to ints
        n = Math.round(number);
    } else {
        n = toLocaleString(number.toPrecision(precision), options.locale);
    }
    let unit = BYTES_UNITS[exponent];
    return prefix + n + ' ' + unit;
};
export const formatNumber = (number, format = 'de-DE')=>{
    return new Intl.NumberFormat(format).format(number);
};
export const formatRegion = (region, locale = 'de')=>{
    try {
        let r = new Intl.DisplayNames([
            locale
        ], {
            type: 'region'
        });
        return r.of(region.trim().toUpperCase());
    } catch (err) {
        return region;
    }
};
export const createDurationTime = (dur)=>{
    const hours = Math.floor(dur / 60);
    let minutes = dur % 60;
    if (minutes < 10) {
        minutes = `0${minutes}`;
    }
    return `${hours}:${minutes} Std.`;
};
export const prettyPrintXML = (xmlString, indentIncrement = '\t')=>{
    /**
   * Formats and pretty-prints an XML string. This function takes an input XML string, parses it to create a DOM document,
   * and processes each node to ensure a neatly formatted output with proper indentation and XML escape handling.
   *
   * @param {string} xmlString - The XML string to be formatted and pretty-printed.
   * @returns {string} The formatted XML string with proper indentation and escaped characters.
   *
   * Note: The function assumes that the input XML string might begin with the standard XML declaration
   * '<?xml version="1.0" encoding="UTF-8"?>' and retains it in the output if present.
   * The output uses tab characters ('\t') for indentation.
   */ const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml');
    function escapeXML(str) {
        return str.replace(/[<>&'"]/g, function(char) {
            switch(char){
                case '<':
                    return '&lt;';
                case '>':
                    return '&gt;';
                case '&':
                    return '&amp;';
                case "'":
                    return '&apos;';
                case '"':
                    return '&quot;';
                default:
                    return char;
            }
        });
    }
    function processNode(node, indent) {
        let xml = '';
        const newline = '\n';
        switch(node.nodeType){
            case Node.ELEMENT_NODE:
                xml += newline + indent + '<' + node.nodeName;
                // Copy attributes with proper escaping
                for(let i = 0; i < node.attributes.length; i++){
                    const attr = node.attributes[i];
                    xml += ' ' + attr.name + '="' + escapeXML(attr.value) + '"';
                }
                xml += '>';
                // Recursively process child nodes
                let hasElementChild = false;
                for(let i = 0; i < node.childNodes.length; i++){
                    const child = node.childNodes[i];
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        hasElementChild = true;
                    }
                    xml += processNode(child, indent + indentIncrement);
                }
                if (hasElementChild) {
                    xml += newline + indent;
                }
                xml += '</' + node.nodeName + '>';
                break;
            case Node.TEXT_NODE:
                const textContent = node.nodeValue;
                const normalizedText = textContent.replace(/[\n\r\t]/g, '');
                if (normalizedText.trim() === '') {
                    // Keep single whitespaces
                    if (normalizedText === ' ') {
                        xml += ' ';
                    }
                } else {
                    xml += escapeXML(textContent);
                }
                break;
            case Node.COMMENT_NODE:
                xml += newline + indent + '<!--' + escapeXML(node.nodeValue) + '-->';
                break;
            case Node.PROCESSING_INSTRUCTION_NODE:
                xml += newline + indent + '<?' + node.nodeName + ' ' + escapeXML(node.nodeValue) + '?>';
                break;
            default:
                break;
        }
        return xml;
    }
    let formattedXML = processNode(xmlDoc.documentElement, '');
    if (xmlString.startsWith('<?xml version="1.0" encoding="UTF-8"?>')) {
        formattedXML = '<?xml version="1.0" encoding="UTF-8"?>' + formattedXML;
    } else {
        formattedXML = lstrip(formattedXML);
    }
    return formattedXML;
};
